@import 'tailwindcss';
@import './styles/tokens.css';
@import './styles/typography.css';
@import './styles/layout.css';

/* Map the semantic tokens (tokens.css) onto Tailwind v4 utilities, so `bg-card` /
   `text-muted-foreground` / `border-border` mean what they mean in monza and in shadcn — the
   vocabulary the copied components are written in. `inline` keeps the generated utilities
   pointing AT the CSS vars rather than baking in a value, so the tokens stay the one seam. */
@theme inline {
  --color-background: var(--background);
  --color-foreground: var(--foreground);
  --color-card: var(--card);
  --color-card-foreground: var(--card-foreground);
  --color-popover: var(--popover);
  --color-popover-foreground: var(--popover-foreground);
  --color-sidebar: var(--sidebar);
  --color-sidebar-foreground: var(--sidebar-foreground);
  --color-border: var(--border);
  --color-input: var(--input);
  --color-muted: var(--muted);
  --color-muted-foreground: var(--muted-foreground);
  --color-secondary: var(--secondary);
  --color-secondary-foreground: var(--secondary-foreground);
  --color-accent: var(--accent);
  --color-accent-foreground: var(--accent-foreground);
  --color-primary: var(--primary);
  --color-primary-foreground: var(--primary-foreground);
  --color-destructive: var(--destructive);
  --color-warning: var(--warning);
  --color-success: var(--success);
  --color-ring: var(--ring);

  --font-standard: var(--font-standard);
  --font-number: var(--font-number);

  --radius-card: var(--radius);
}

/* The default border colour, so a bare `border` is the token and not Tailwind's grey. */
@layer base {
  * {
    border-color: var(--border);
  }
  /* One focus treatment everywhere: solid, 2px, Primary — distinct from hover, never a
     translucent ring. :focus-visible (not :focus) so a mouse click doesn't ring a button. */
  *:focus-visible {
    outline: var(--ring-width) solid var(--ring);
    outline-offset: 2px;
  }
}

html,
body,
#root {
  height: 100%;
}

body {
  margin: 0;
  background: var(--background);
  color: var(--foreground);
  font-family: var(--font-standard);
  font-size: var(--fs-base);
  line-height: 1.5;
  -webkit-font-smoothing: antialiased;
}

/* ───────────────────────────── CLICKABLE OPTIONS ─────────────────────────────
   ONE hover treatment for every clickable option in the portal: side-menu entries, list rows,
   tree nodes, tabs, picker options, chrome icon buttons. Anything a click SELECTS carries
   `.admin-option`; the selected one carries `data-active`; one that cannot be clicked (yet)
   carries `data-disabled`.

   Hover is --nav-hover, one notch LIGHTER than the selected fill (--secondary), so hover reads
   as "the selected tint, not yet selected". Selection beats hover — hovering the row you are
   already on must not restyle it. A non-clickable row takes no hover at all: a hover implies
   "clickable", which it is not.

   ★★ THE RESTING FILL LIVES HERE, NOT IN A COMPONENT'S INLINE STYLE OBJECT. This app styles
   almost everything with inline `CSSProperties`, and an inline declaration outranks any
   stylesheet rule — an inline `background: 'transparent'` silently kills the :hover below. It
   did exactly that in the side menu, whose hover rule sat here correct and dead for weeks. If a
   state lives in CSS, its resting value must live in CSS too.

   ⚠ ORDER MATTERS: :hover, [data-active] and [data-disabled] all have the same specificity
   (0,2,0), so the LATER rule wins. Do not reorder these blocks.

   ⛔⛔ AND THAT IS WHY THE `@media (hover: hover)` BELOW IS WRAPPED IN PLACE RATHER THAN HOISTED.
   A media query changes neither specificity nor source order, so gating the hover where it already
   stood is invisible to the cascade — but collecting the portal's hover rules into one block at the
   foot of the sheet, which is the tidier-looking arrangement and the obvious next edit, would move
   :hover BELOW [data-active] and light up the row you are already on. */
.admin-option {
  background: transparent;
}
@media (hover: hover) {
  .admin-option:hover {
    background: var(--nav-hover);
  }
}
.admin-option[data-active] {
  background: var(--secondary);
}
.admin-option[data-disabled] {
  background: transparent;
}

/* ───────────────────────────── BUTTONS ─────────────────────────────
   Three kinds, one hover hint each. An option SELECTS (above); a button ACTS.

     .admin-btn         neutral / ghost / icon — hover to the same --nav-hover tint
     .admin-btn-primary the green CTA (Save, Publish, Sign in) — hover STEPS DOWN the Primary
                        ramp to --primary-hover, never an opacity fade
     .admin-btn-danger  destructive outline (Delete, Cancel now, Refund) — hover to
                        --destructive-hover, because a green tint on Delete would be a lie

   `:not(:disabled)` is the whole disabled story: a button that cannot be pressed must not
   answer the mouse. The BLOCKED CTA variants (publishBtnBlocked / primaryBtnOff / btnOff /
   saveBtnBlocked) additionally KEEP their inline background on purpose — inline outranks these
   rules, so a blocked button is inert twice over.

   Same rule as the options above: the resting fill lives HERE, so the component's inline style
   object must not declare `background` (it would outrank the hover and kill it). */
.admin-btn {
  background: transparent;
}
@media (hover: hover) {
  .admin-btn:not(:disabled):hover {
    background: var(--nav-hover);
  }
}

.admin-btn-primary {
  background: var(--primary);
}
@media (hover: hover) {
  .admin-btn-primary:not(:disabled):hover {
    background: var(--primary-hover);
  }
}

.admin-btn-danger {
  background: transparent;
}
@media (hover: hover) {
  .admin-btn-danger:not(:disabled):hover {
    background: var(--destructive-hover);
  }
}

/* ★ ATTRIBUTE PILL — portal-wide (David, 2026-07-23).
   A pill that states an ATTRIBUTE of the thing beside it: a content entry's type ("Film",
   "Series", "Episode"), a supplier's kind, and so on. It takes the same amber treatment as the
   landing page's "There is no Publish step" notice — dark yellow outline, pale yellow fill —
   so an attribute reads consistently wherever it appears.

   ⚠ NOT for STATUS. A pill that reports a state rather than a property — "no delivery
   recorded" (warning), "12 files" (success), "past due" (destructive) — keeps its semantic
   colour, because those colours ARE the message. Amber on a failing state would say the wrong
   thing. The test is whether the pill would still be true if nothing had happened yet: an
   attribute would, a status would not.

   ⚠ Deliberately NOT bold (David): the pill's job is to label quietly, and weight here competes
   with the row's own title for attention.

   ⚠ Any inline `background`/`border`/`color` on the element outranks this rule — the same trap
   documented on .admin-option above. Leave those props undefined at the call site. */
.admin-pill {
  border: 1px solid var(--accent2-base);
  background: var(--accent2-hint);
  color: var(--accent2-nearblack);
  border-radius: 999px;
  padding: 0 8px;
  font-size: var(--fs-xs);
  font-weight: 400;
  white-space: nowrap;
}

/* Hide the native scrollbar on a region that mounts our own overlay thumb (OverlayScrollbar).
   The overlay bar draws OVER the content and reserves no layout width, so the native bar must
   go — otherwise the region pays for two. */
.yoreflix-no-scrollbar {
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* legacy Edge */
}
.yoreflix-no-scrollbar::-webkit-scrollbar {
  width: 0;
  height: 0;
}

/* ───────────────────────────── PRESS FEEDBACK (touch) ─────────────────────────────
   ⛔ EVERY CUE IN THIS PORTAL IS A `:hover`, AND A TOUCH DEVICE CANNOT HOVER — while Tailwind's
   preflight has already removed the OS tap flash. So a tap on a phone or a tablet produced nothing
   at all.

   ⛔⛔ "NO HOVER" IS NOT WHAT A TABLET ACTUALLY DOES, AND THE DIFFERENCE WAS A SECOND BUG (David,
   2026-08-20). A touch browser has no hover STATE to track, so it fakes one: it applies :hover to
   the element you tapped and leaves it applied until you tap somewhere else. The cue therefore
   arrived late, on the wrong element, and stayed — a menu row or a Delete button left highlighted
   as though the finger were still on it. That is worse than nothing, because a stuck highlight on
   `.admin-option` is indistinguishable from the [data-active] fill that means "this is the screen
   you are on". Hence `@media (hover: hover)` on all four hover rules above: they now run only where
   a real pointer can leave as well as arrive, and touch gets the press cue below and nothing else. `data-pressed` is set on pointerdown by `@yoreflix/core/ui/pressFeedback` (installed in
   main.tsx); see that file for why this cannot be `:active`.

   ⛔⛔ THE WASH IS DARK HERE AND CREAM IN THE VIEWER, AND THAT IS THE WHOLE RULE: a press must add
   CONTRAST, not move in a fixed direction. The viewer is black, so it lightens with `--c-cream`
   (David set that colour on 2026-08-20); this portal is a near-white
   (`--background: rgba(0,53,0,0.035)`), where a cream wash would be invisible — the same reasoning
   as the text-field icons, which are measured off the field and darken on white while lightening on
   the viewer's black. ⛔ Do NOT "make it consistent" by copying the viewer's cream here: the two
   values are consistent, in the only sense that matters.

   ⛔ AN INSET `box-shadow`, NOT A `background`. This app styles almost everything with inline
   `CSSProperties`, and an inline declaration outranks any stylesheet rule — the note above the
   `.admin-option` block records a hover rule that sat here correct and dead for weeks for exactly
   that reason. A shadow is painted OVER whatever set the background, follows `border-radius` for
   free, and sits below the text so nothing is washed out. */
:root {
  --press-wash: rgba(0, 0, 0, 0.1);
}

[data-pressed] {
  box-shadow: inset 0 0 0 100vmax var(--press-wash);
}
